/**
 * Bulk charge state machine
 *
 * idle -> loadingSummary -> summary -> charging -> done
 *
 * Pure and exported so the transitions can be tested without rendering. The
 * important invariant it enforces: once a summary exists, the parsed id list is
 * the source of truth and `rawInput` is regenerated from it, so the textarea
 * always shows exactly what will be charged.
 */

import {
  CONSECUTIVE_FAILURE_ABORT_THRESHOLD,
  MAX_IDS,
  chargePhase,
  outcomeClass,
  rowOutcome
} from './constants';
import { parseAmbassadorIds, serializeAmbassadorIds } from './helpers';
import {
  BulkChargeState,
  ChargeVerification,
  EventAmbassador,
  OutcomeClass,
  RowEligibility,
  SummaryRow
} from './types';

export const initialState: BulkChargeState = {
  phase: chargePhase.idle,
  rawInput: '',
  ids: [],
  parseFeedback: { invalid: [], duplicates: [], overflow: 0 },
  rows: {},
  batchId: null,
  loadError: null,
  confirmListCorrect: false,
  confirmIrreversible: false,
  stopRequested: false,
  consecutiveIndeterminate: 0,
  abortedByBreaker: false,
  summaryInvalidated: false
};

/**
 * Parse for display only. `rawInput` is left exactly as typed: canonicalising
 * mid-keystroke would fight the operator's cursor. Generating is what commits
 * the canonical form.
 */
const parseForDisplay = (rawInput: string) => {
  const parsed = parseAmbassadorIds(rawInput);
  const overflow = Math.max(0, parsed.ids.length - MAX_IDS);
  return {
    ids: parsed.ids.slice(0, MAX_IDS),
    parseFeedback: { invalid: parsed.invalid, duplicates: parsed.duplicates, overflow }
  };
};

export type BulkChargeAction =
  | { type: 'SET_RAW_INPUT'; rawInput: string }
  | { type: 'GENERATE_START' }
  | { type: 'GENERATE_FAILURE'; error: string }
  | { type: 'GENERATE_SUCCESS'; rows: SummaryRow[] }
  | { type: 'REMOVE_ID'; id: number }
  | { type: 'SET_CONFIRM'; field: 'confirmListCorrect' | 'confirmIrreversible'; value: boolean }
  | { type: 'CONFIRM_CHARGE'; batchId: string }
  | { type: 'ROW_CHARGING'; id: number }
  | {
      type: 'ROW_RESULT';
      id: number;
      outcome: OutcomeClass;
      errorCode: string | null;
      httpStatus: number | null;
      attemptedAtUtc: string;
    }
  | { type: 'ROW_VERIFIED'; id: number; verification: ChargeVerification }
  | { type: 'ROW_REFRESH_START'; id: number }
  | {
      type: 'ROW_REFRESHED';
      id: number;
      ambassador: EventAmbassador | null;
      eligibility: RowEligibility;
      amount: SummaryRow['amount'];
      verification: ChargeVerification | null;
    }
  | { type: 'REQUEST_STOP' }
  | { type: 'CHARGE_FINISHED' }
  | { type: 'CLEAR' }
  | { type: 'START_NEW_BATCH' };

/** Editing the ids invalidates any summary built from the old list. */
const resetSummary = (state: BulkChargeState, rawInput: string): BulkChargeState => ({
  ...state,
  phase: chargePhase.idle,
  rawInput,
  ...parseForDisplay(rawInput),
  rows: {},
  loadError: null,
  confirmListCorrect: false,
  confirmIrreversible: false,
  abortedByBreaker: false,
  // Only warn about a stale summary if one actually existed.
  summaryInvalidated: state.summaryInvalidated || state.phase === chargePhase.summary
});

export const bulkChargeReducer = (
  state: BulkChargeState,
  action: BulkChargeAction
): BulkChargeState => {
  switch (action.type) {
    case 'SET_RAW_INPUT': {
      // Locked while charges are in flight or the run has finished.
      if (state.phase === chargePhase.charging || state.phase === chargePhase.done) {
        return state;
      }
      return resetSummary(state, action.rawInput);
    }

    case 'GENERATE_START': {
      const { ids, parseFeedback } = parseForDisplay(state.rawInput);

      return {
        ...state,
        phase: ids.length ? chargePhase.loadingSummary : chargePhase.idle,
        // Canonicalise the paste so the textarea matches the list exactly.
        rawInput: ids.length ? serializeAmbassadorIds(ids) : state.rawInput,
        ids,
        parseFeedback,
        rows: {},
        loadError: null,
        confirmListCorrect: false,
        confirmIrreversible: false,
        abortedByBreaker: false,
        summaryInvalidated: false
      };
    }

    case 'GENERATE_FAILURE':
      return { ...state, phase: chargePhase.idle, rows: {}, loadError: action.error };

    case 'GENERATE_SUCCESS': {
      const rows: Record<number, SummaryRow> = {};
      action.rows.forEach((row) => {
        rows[row.id] = row;
      });
      return { ...state, phase: chargePhase.summary, rows, loadError: null };
    }

    case 'REMOVE_ID': {
      if (state.phase === chargePhase.charging || state.phase === chargePhase.done) {
        return state;
      }
      // ids, rows and rawInput move together in one action. Doing this as
      // separate updates, or by editing the textarea text, is how an id the
      // operator believed they removed survives and gets charged.
      const ids = state.ids.filter((id) => id !== action.id);
      const rows = { ...state.rows };
      delete rows[action.id];

      return {
        ...state,
        ids,
        rows,
        rawInput: serializeAmbassadorIds(ids)
      };
    }

    case 'SET_CONFIRM':
      if (state.phase !== chargePhase.summary) {
        return state;
      }
      return { ...state, [action.field]: action.value };

    case 'CONFIRM_CHARGE':
      // Ignore a second confirm: the run is already going.
      if (state.phase !== chargePhase.summary) {
        return state;
      }
      return {
        ...state,
        phase: chargePhase.charging,
        batchId: action.batchId,
        stopRequested: false,
        consecutiveIndeterminate: 0,
        abortedByBreaker: false
      };

    case 'ROW_CHARGING': {
      const row = state.rows[action.id];
      if (!row) {
        return state;
      }
      return {
        ...state,
        rows: { ...state.rows, [action.id]: { ...row, outcome: rowOutcome.charging } }
      };
    }

    case 'ROW_RESULT': {
      const row = state.rows[action.id];
      if (!row) {
        return state;
      }
      const consecutiveIndeterminate =
        action.outcome === outcomeClass.indeterminate ? state.consecutiveIndeterminate + 1 : 0;

      return {
        ...state,
        consecutiveIndeterminate,
        abortedByBreaker:
          state.abortedByBreaker ||
          consecutiveIndeterminate >= CONSECUTIVE_FAILURE_ABORT_THRESHOLD,
        rows: {
          ...state.rows,
          [action.id]: {
            ...row,
            outcome: action.outcome,
            errorCode: action.errorCode,
            httpStatus: action.httpStatus,
            attemptedAtUtc: action.attemptedAtUtc
          }
        }
      };
    }

    case 'ROW_VERIFIED': {
      const row = state.rows[action.id];
      if (!row) {
        return state;
      }
      return {
        ...state,
        rows: { ...state.rows, [action.id]: { ...row, verification: action.verification } }
      };
    }

    case 'ROW_REFRESH_START': {
      const row = state.rows[action.id];
      if (!row) {
        return state;
      }
      return { ...state, rows: { ...state.rows, [action.id]: { ...row, isRefreshing: true } } };
    }

    case 'ROW_REFRESHED': {
      const row = state.rows[action.id];
      if (!row) {
        return state;
      }
      return {
        ...state,
        rows: {
          ...state.rows,
          [action.id]: {
            ...row,
            ambassador: action.ambassador,
            eligibility: action.eligibility,
            amount: action.amount,
            verification: action.verification ?? row.verification,
            // A refresh updates what we know about the ambassador. It never
            // resets the outcome: that record of what was attempted stands.
            isRefreshing: false,
            fetchError: action.ambassador ? null : row.fetchError
          }
        }
      };
    }

    case 'REQUEST_STOP':
      return { ...state, stopRequested: true };

    case 'CHARGE_FINISHED': {
      // Anything still pending was never attempted, so mark it skipped rather
      // than leaving it looking like it is about to happen.
      const rows = { ...state.rows };
      Object.keys(rows).forEach((key) => {
        const id = Number(key);
        if (rows[id].outcome === rowOutcome.pending || rows[id].outcome === rowOutcome.charging) {
          rows[id] = { ...rows[id], outcome: rowOutcome.blocked };
        }
      });
      return { ...state, phase: chargePhase.done, rows };
    }

    case 'CLEAR':
      if (state.phase === chargePhase.charging) {
        return state;
      }
      return { ...initialState };

    case 'START_NEW_BATCH':
      return { ...initialState };

    default:
      return state;
  }
};
